import { CSSResult, unsafeCSS } from "lit";

/**
 * Create an object that can be passed to cssVariableMap
 * to define CSS variables in a style tag. The default properties
 * are the ones exposed for customization in the remote config interface.
 * @example
 * const variables = {
 *  ...customizableTextJs("widget-header",
 *  "widget-border-color": "red"
 *  };
 *
 *  html`<style>:host { ${_cssVariableMap(variables)} }</style>`;
 * @returns {{[p: string]: string}}
 */
export const defineCustomText = (
  namespace = "",
  {
    fontFamily = "inherit",
    fontStyle = "inherit",
    fontSize = "inherit",
    fontWeight = "inherit",
    textTransform = "none",
    marginTop = "0",
    lineHeight = "inherit",
    color = "inherit",
  } = {}
): Record<string, string> => {
  const prefix = namespace.startsWith("--beam-") ? namespace : `--beam-${namespace}`;
  return {
    [`${prefix}-fontFamily`]: fontFamily,
    [`${prefix}-fontStyle`]: fontStyle,
    [`${prefix}-fontSize`]: fontSize,
    [`${prefix}-fontWeight`]: fontWeight,
    [`${prefix}-textTransform`]: textTransform,
    [`${prefix}-marginTop`]: marginTop,
    [`${prefix}-lineHeight`]: lineHeight,
    [`${prefix}-color`]: color,
  };
};

/**
 * Returns a string that defines css properties based on variables,
 * which can be used in a css`` template. The default properties are the
 * ones exposed for customization in the remote config interface.
 * @example
 * css`.widget-header { ${customizableTextCss("widget-header")} border: none; }`
 * @returns {CSSResult}
 */
export const useCustomText = (
  namespace = "",
  {
    fontFamily = "inherit",
    fontStyle = "inherit",
    fontSize = "inherit",
    fontWeight = "inherit",
    textTransform = "none",
    marginTop = "0",
    lineHeight = "inherit",
    color = "inherit",
  } = {}
): CSSResult => {
  const prefix = namespace.startsWith("--beam-") ? namespace : `--beam-${namespace}`;
  return unsafeCSS(`\
        font-family: var(${prefix}-fontFamily, ${fontFamily});
        font-style: var(${prefix}-fontStyle, ${fontStyle});
        font-size: var(${prefix}-fontSize, ${fontSize});
        font-weight: var(${prefix}-fontWeight, ${fontWeight});
        text-transform: var(${prefix}-textTransform, ${textTransform});
        margin-top: var(${prefix}-marginTop, ${marginTop});
        line-height: var(${prefix}-lineHeight, ${lineHeight});
        color: var(${prefix}-color, ${color});
    `);
};

/**
 * Same as useCustomText except returns an object that can be passed to styleMap
 */
export const useCustomTextMap = (
  namespace = "",
  {
    fontSize = "inherit",
    fontStyle = "inherit",
    fontWeight = "inherit",
    textTransform = "none",
    marginTop = "0",
    lineHeight = "inherit",
  } = {}
): Record<string, string> => {
  const prefix = namespace.startsWith("--beam-") ? namespace : `--beam-${namespace}`;
  return {
    fontSize: `var(${prefix}-fontSize, ${fontSize})`,
    fontStyle: `var(${prefix}-fontStyle, ${fontStyle})`,
    fontWeight: `var(${prefix}-fontWeight, ${fontWeight})`,
    textTransform: `var(${prefix}-textTransform, ${textTransform})`,
    marginTop: `var(${prefix}-marginTop, ${marginTop})`,
    lineHeight: `var(${prefix}-lineHeight, ${lineHeight})`,
  };
};
